import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Assessment as ReviewIcon,
  Star as RatingIcon,
  TrendingUp as GrowthIcon,
  School as DevelopmentIcon,
  Feedback as FeedbackIcon,
  CheckCircle as CompleteIcon,
  Schedule as PendingIcon,
  Edit as EditIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

interface PerformanceReview {
  id: number;
  employee_name: string;
  reviewer_name: string;
  review_type: string;
  status: 'draft' | 'pending' | 'in_progress' | 'completed' | 'approved';
  overall_rating?: string;
  overall_score?: number;
  review_period_start: string;
  review_period_end: string;
  due_date: string;
  strengths?: string;
  areas_for_improvement?: string;
  development_goals?: string[];
  manager_comments?: string;
  employee_comments?: string;
}

interface CompetencyRating {
  competency: string;
  description: string;
  self_rating: number;
  manager_rating?: number;
  comments: string;
}

const PerformanceReview: React.FC = () => {
  const [reviews, setReviews] = useState<PerformanceReview[]>([]);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<PerformanceReview | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [competencyRatings, setCompetencyRatings] = useState<CompetencyRating[]>([]);
  
  const [reviewData, setReviewData] = useState({
    overall_rating: '',
    overall_score: 0,
    strengths: '',
    areas_for_improvement: '',
    development_goals: [] as string[],
    manager_comments: '',
    employee_comments: ''
  });

  const competencies = [
    { name: 'Communication', description: 'Ability to convey information clearly and effectively' },
    { name: 'Leadership', description: 'Ability to guide and inspire others' },
    { name: 'Problem Solving', description: 'Ability to identify and resolve issues effectively' },
    { name: 'Teamwork', description: 'Ability to work collaboratively with others' },
    { name: 'Technical Skills', description: 'Job-specific technical competencies' },
    { name: 'Customer Focus', description: 'Commitment to meeting customer needs' },
    { name: 'Innovation', description: 'Ability to generate creative solutions' },
    { name: 'Time Management', description: 'Ability to prioritize and manage time effectively' }
  ];

  useEffect(() => {
    fetchReviews();
    initializeCompetencyRatings();
  }, []);

  const fetchReviews = async () => {
    // Mock data
    setReviews([
      {
        id: 1,
        employee_name: 'John Smith',
        reviewer_name: 'Sarah Manager',
        review_type: 'Annual Review',
        status: 'completed',
        overall_rating: 'exceeds_expectations',
        overall_score: 4.2,
        review_period_start: '2023-01-01',
        review_period_end: '2023-12-31',
        due_date: '2024-01-15',
        strengths: 'Excellent technical skills, strong leadership qualities, consistently delivers high-quality work',
        areas_for_improvement: 'Could improve time management skills and delegate more effectively',
        development_goals: ['Complete leadership training program', 'Improve project management skills'],
        manager_comments: 'John has shown exceptional growth this year and is ready for increased responsibilities.',
        employee_comments: 'I appreciate the feedback and am committed to working on the identified areas for improvement.'
      },
      {
        id: 2,
        employee_name: 'John Smith',
        reviewer_name: 'Sarah Manager',
        review_type: 'Quarterly Review',
        status: 'pending',
        review_period_start: '2024-01-01',
        review_period_end: '2024-03-31',
        due_date: '2024-04-15'
      }
    ]);
  };

  const initializeCompetencyRatings = () => {
    setCompetencyRatings(
      competencies.map(comp => ({
        competency: comp.name,
        description: comp.description,
        self_rating: 0,
        manager_rating: undefined,
        comments: ''
      }))
    );
  };

  const handleStartReview = (review: PerformanceReview) => {
    setSelectedReview(review);
    setReviewData({
      overall_rating: review.overall_rating || '',
      overall_score: review.overall_score || 0,
      strengths: review.strengths || '',
      areas_for_improvement: review.areas_for_improvement || '',
      development_goals: review.development_goals || [],
      manager_comments: review.manager_comments || '',
      employee_comments: review.employee_comments || ''
    });
    setReviewDialogOpen(true);
    setActiveStep(0);
  };

  const handleSaveReview = async () => {
    if (!selectedReview) return;

    setReviews(prev => prev.map(review => 
      review.id === selectedReview.id 
        ? { ...review, ...reviewData, status: 'completed' as const }
        : review
    ));

    setReviewDialogOpen(false);
    setSelectedReview(null);
  };

  const updateCompetencyRating = (competency: string, field: string, value: any) => {
    setCompetencyRatings(prev => prev.map(rating => 
      rating.competency === competency 
        ? { ...rating, [field]: value }
        : rating
    ));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'approved': return 'success';
      case 'in_progress': return 'info';
      case 'pending': return 'warning';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return <CompleteIcon />;
      case 'in_progress':
      case 'pending':
        return <PendingIcon />;
      default:
        return <EditIcon />;
    }
  };

  const getRatingLabel = (rating: string) => {
    switch (rating) {
      case 'outstanding': return 'Outstanding (5)';
      case 'exceeds_expectations': return 'Exceeds Expectations (4)';
      case 'meets_expectations': return 'Meets Expectations (3)';
      case 'below_expectations': return 'Below Expectations (2)';
      case 'unsatisfactory': return 'Unsatisfactory (1)';
      default: return 'Not Rated';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const steps = [
    'Self Assessment',
    'Goal Review',
    'Development Planning',
    'Overall Rating'
  ];

  return (
    <Box sx={{ p: 3, pt: 2 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        mt: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Performance Reviews
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Complete and track your performance evaluations
          </Typography>
        </Box>
      </Box>

      {/* Pending Review Alert */}
      {reviews.some(r => r.status === 'pending') && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="subtitle2" gutterBottom>
            📋 Performance Review Due
          </Typography>
          <Typography variant="body2">
            Your quarterly performance review is due on April 15th. Please complete your self-assessment to begin the review process.
          </Typography>
        </Alert>
      )}

      {/* Review Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ReviewIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="primary">
                {reviews.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Reviews
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <RatingIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {reviews.find(r => r.overall_score)?.overall_score?.toFixed(1) || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Latest Rating
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PendingIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {reviews.filter(r => r.status === 'pending').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Reviews
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reviews List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Review History</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Review Type</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reviews.map((review) => (
                  <TableRow key={review.id}>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {review.review_type}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Reviewer: {review.reviewer_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(review.review_period_start)} - {formatDate(review.review_period_end)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(review.due_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {review.overall_score ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6" color="primary">
                            {review.overall_score.toFixed(1)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            / 5.0
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not Rated
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={review.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(review.status) as any}
                        size="small"
                        icon={getStatusIcon(review.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {review.status === 'pending' ? (
                          <Button 
                            size="small" 
                            startIcon={<EditIcon />}
                            onClick={() => handleStartReview(review)}
                          >
                            Start Review
                          </Button>
                        ) : (
                          <Button 
                            size="small" 
                            startIcon={<ViewIcon />}
                            onClick={() => handleStartReview(review)}
                          >
                            View Review
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedReview?.review_type} - {selectedReview?.employee_name}
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 2 }}>
            {/* Self Assessment */}
            <Step>
              <StepLabel>Self Assessment</StepLabel>
              <StepContent>
                <Typography variant="h6" gutterBottom>Competency Self-Assessment</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Rate yourself on each competency using a 1-5 scale and provide comments.
                </Typography>
                
                <Grid container spacing={2}>
                  {competencyRatings.map((rating) => (
                    <Grid item xs={12} key={rating.competency}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>
                            {rating.competency}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {rating.description}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Typography variant="body2">Self Rating:</Typography>
                            <Rating
                              value={rating.self_rating}
                              onChange={(e, value) => updateCompetencyRating(rating.competency, 'self_rating', value || 0)}
                              max={5}
                            />
                            <Typography variant="body2">({rating.self_rating}/5)</Typography>
                          </Box>
                          
                          <TextField
                            fullWidth
                            label="Comments"
                            multiline
                            rows={2}
                            value={rating.comments}
                            onChange={(e) => updateCompetencyRating(rating.competency, 'comments', e.target.value)}
                            placeholder="Provide specific examples and context..."
                            size="small"
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                <Box sx={{ mt: 2 }}>
                  <Button onClick={() => setActiveStep(1)}>
                    Next
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* Goal Review */}
            <Step>
              <StepLabel>Goal Review</StepLabel>
              <StepContent>
                <Typography variant="h6" gutterBottom>Goal Achievement Review</Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Review your goals from the performance period and assess your achievements.
                </Alert>
                
                <TextField
                  fullWidth
                  label="Key Achievements"
                  multiline
                  rows={4}
                  value={reviewData.strengths}
                  onChange={(e) => setReviewData(prev => ({ ...prev, strengths: e.target.value }))}
                  placeholder="Describe your key achievements and successes during this period..."
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="Challenges and Learning"
                  multiline
                  rows={4}
                  value={reviewData.areas_for_improvement}
                  onChange={(e) => setReviewData(prev => ({ ...prev, areas_for_improvement: e.target.value }))}
                  placeholder="Describe challenges faced and what you learned from them..."
                />
                
                <Box sx={{ mt: 2 }}>
                  <Button onClick={() => setActiveStep(0)} sx={{ mr: 1 }}>
                    Back
                  </Button>
                  <Button onClick={() => setActiveStep(2)}>
                    Next
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* Development Planning */}
            <Step>
              <StepLabel>Development Planning</StepLabel>
              <StepContent>
                <Typography variant="h6" gutterBottom>Future Development Goals</Typography>
                
                <TextField
                  fullWidth
                  label="Development Goals for Next Period"
                  multiline
                  rows={4}
                  value={reviewData.development_goals.join('\n')}
                  onChange={(e) => setReviewData(prev => ({ 
                    ...prev, 
                    development_goals: e.target.value.split('\n').filter(goal => goal.trim()) 
                  }))}
                  placeholder="List your development goals for the next review period (one per line)..."
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="Additional Comments"
                  multiline
                  rows={3}
                  value={reviewData.employee_comments}
                  onChange={(e) => setReviewData(prev => ({ ...prev, employee_comments: e.target.value }))}
                  placeholder="Any additional comments or feedback..."
                />
                
                <Box sx={{ mt: 2 }}>
                  <Button onClick={() => setActiveStep(1)} sx={{ mr: 1 }}>
                    Back
                  </Button>
                  <Button onClick={() => setActiveStep(3)}>
                    Next
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* Overall Rating */}
            <Step>
              <StepLabel>Overall Rating</StepLabel>
              <StepContent>
                <Typography variant="h6" gutterBottom>Review Summary</Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Overall Self-Rating</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Rating
                      value={reviewData.overall_score}
                      onChange={(e, value) => setReviewData(prev => ({ ...prev, overall_score: value || 0 }))}
                      max={5}
                      size="large"
                    />
                    <Typography variant="h6">({reviewData.overall_score}/5)</Typography>
                  </Box>
                </Box>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Overall Performance Rating</InputLabel>
                  <Select
                    value={reviewData.overall_rating}
                    onChange={(e) => setReviewData(prev => ({ ...prev, overall_rating: e.target.value }))}
                  >
                    <MenuItem value="outstanding">Outstanding (5)</MenuItem>
                    <MenuItem value="exceeds_expectations">Exceeds Expectations (4)</MenuItem>
                    <MenuItem value="meets_expectations">Meets Expectations (3)</MenuItem>
                    <MenuItem value="below_expectations">Below Expectations (2)</MenuItem>
                    <MenuItem value="unsatisfactory">Unsatisfactory (1)</MenuItem>
                  </Select>
                </FormControl>

                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Review Complete</Typography>
                  <Typography variant="body2">
                    Your self-assessment is complete. Your manager will review your submission and provide their feedback.
                  </Typography>
                </Alert>
                
                <Box sx={{ mt: 2 }}>
                  <Button onClick={() => setActiveStep(2)} sx={{ mr: 1 }}>
                    Back
                  </Button>
                  <Button variant="contained" onClick={handleSaveReview}>
                    Submit Review
                  </Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerformanceReview;
